import { useState } from "react";
import { useEmployeeLeave, useHrInvalidate } from "@/hooks/use-hr-queries";
import { hrEmployeeApi } from "@/lib/api/hr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Page, Panel, QueryGate, Stat, StatusBadge } from "@/components/shell/page";

export default function EmployeeLeavePage() {
  const { data, isLoading, error } = useEmployeeLeave();
  const invalidate = useHrInvalidate();
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMessage("");
    setFailed(false);
    try {
      await hrEmployeeApi.requestLeave({
        leave_type_id: Number(leaveTypeId),
        start_date: start,
        end_date: end || start,
        reason,
      });
      setReason("");
      invalidate();
      setMessage("Leave request submitted.");
    } catch (err) {
      setFailed(true);
      setMessage(err instanceof Error ? err.message : "Could not submit leave.");
    }
  }

  return (
    <QueryGate loading={isLoading} error={error || !data ? "Could not load leave." : null}>
      {data && (
        <Page title="Leave" description="Balances come from the ledger. A request does not reduce them until it is approved.">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {(data.items ?? []).map((item: any) => (
              <Stat
                key={item.id}
                label={item.name}
                value={item.available}
                hint={`${item.pending} pending · ${item.used} used`}
              />
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-5">
            <Panel title="Request leave" className="lg:col-span-2">
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="leave-type">Type</Label>
                  <select
                    id="leave-type"
                    className="field-control"
                    value={leaveTypeId}
                    onChange={(e) => setLeaveTypeId(e.target.value)}
                    required
                  >
                    <option value="">Select a type</option>
                    {(data.types ?? []).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="leave-from">From</Label>
                    <Input id="leave-from" type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="leave-to">To</Label>
                    <Input id="leave-to" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="leave-reason">Reason</Label>
                  <Textarea id="leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                <Button type="submit">Submit request</Button>
                {message && (
                  <p className={failed ? "text-sm text-red-700" : "text-sm text-emerald-800"} role="status">
                    {message}
                  </p>
                )}
              </form>
            </Panel>

            <Panel title="Requests" className="lg:col-span-3">
              {(data.requests ?? []).length === 0 && (
                <EmptyState title="No requests" body="Leave you apply for will stay here with its status." />
              )}
              <ul className="divide-y">
                {(data.requests ?? []).map((req: any) => (
                  <li key={req.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium">
                        {req.leave_type_name} · {req.start_date} – {req.end_date}
                      </p>
                      {req.reason && <p className="text-sm text-muted-foreground">{req.reason}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={req.status} />
                      {req.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await hrEmployeeApi.cancelLeave(req.id);
                            invalidate();
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>

          <Panel title="Ledger" description="Every credit and debit that makes up the balance.">
            {(data.ledger ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">No leave transactions yet.</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Entry</th>
                    <th className="py-2 text-right font-medium">Amount</th>
                    <th className="py-2 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.ledger ?? []).map((row: any) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-2.5 pr-3">{row.occurred_on}</td>
                      <td className="py-2.5 pr-3">{row.leave_type}</td>
                      <td className="py-2.5 pr-3 capitalize">{String(row.txn_type || "").replace(/_/g, " ")}</td>
                      <td className="py-2.5 text-right tabular-nums">{row.amount}</td>
                      <td className="py-2.5 text-right tabular-nums">{row.balance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </Page>
      )}
    </QueryGate>
  );
}
